import { z } from "zod";

import { api } from "@/redux/api";

import {
	MyConnectionsResponse,
	MyConnectionsResponseSchema,
	User,
	FetchMeResponseSchema,
} from "@/pages/Public/pages/Login/schema/login";

import { TwoFactorAuthentication } from "@/pages/Public/pages/TwoFactorAuth/schema/twoFactorAuth";
import { TwoFactorSetup } from "@/pages/Private/pages/Account/schema/twoFactorSetup";

import { RequestPaginationParams } from "@/types/request-params";

import {
	LinkedinEnrichmentCostPreview,
	LinkedinEnrichmentCostPreviewParams,
	LinkedinEnrichmentCostPreviewSchema,
	LinkedinEnrichmentSettings,
	LinkedinEnrichmentSettingsSchema,
	UpdateLinkedinEnrichmentSettings,
} from "../../pages/Account/schema/linkedin-enrichment";
import {
	ConnectionsFilterOptions,
	ConnectionsFilterOptionsSchema,
} from "../../pages/Account/schema/connections-filters";
import { ChangePassword, UpdateMeBody, Location } from "../../pages/Account/schema/account";
import { OnboardingSettings } from "../../pages/Onboarding/schema/onboarding";
import { OnboardingSettingsV3 } from "../../pages/OnboardingV3/schema/onboarding";

export const ChangePasswordResponseSchema = FetchMeResponseSchema.extend({
	accessToken: z.string().min(1),
	refreshToken: z.string().min(1),
});

// A 401 can beat the response that installs the replacement credential in auth.slice.
const pendingPasswordChanges = new Set<Promise<void>>();

export const afterPendingPasswordChange = (decide: () => void): void => {
	if (pendingPasswordChanges.size) {
		void Promise.all(pendingPasswordChanges).then(decide);
	} else {
		decide();
	}
};

export const accountApi = api.injectEndpoints({
	endpoints: (builder) => ({
		updateMe: builder.mutation<User, UpdateMeBody>({
			query: (body) => ({
				url: "users",
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Me", "ProfilesWithLiSn"],
		}),
		saveOnboardingSettings: builder.mutation<User, OnboardingSettings>({
			query: (body) => ({
				url: "users/onboarding",
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Me", "ProfilesWithLiSn"],
		}),
		updateOnboardingStep: builder.mutation<User, { step: string }>({
			query: (body) => ({
				url: "users/onboarding-step",
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Me"],
		}),
		// Starts the workspace for a wizard run without checkout (V2, PREC-3719):
		// the lists the wizard created and the lead-scoring generation would
		// otherwise wait for a checkout that never happens.
		completeOnboarding: builder.mutation<{ status: string }, void>({
			query: () => ({
				url: "users/onboarding/complete",
				method: "POST",
			}),
			invalidatesTags: ["Me"],
		}),
		// The slim onboarding (V3) for users joining a workspace that is already set
		// up. The URL keeps its pre-PREC-3719 name so open sessions keep working.
		saveSlimOnboardingSettings: builder.mutation<User, OnboardingSettingsV3>({
			query: (body) => ({
				url: "users/onboarding-v2",
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Me", "ProfilesWithLiSn"],
		}),
		getConnectionsFilterOptions: builder.query<ConnectionsFilterOptions, void>({
			query: () => ({
				url: "users/my-connections/filter-options",
				responseSchema: ConnectionsFilterOptionsSchema,
			}),
			providesTags: ["MyConnections"],
		}),
		findMyConnections: builder.query<
			MyConnectionsResponse,
			RequestPaginationParams & Record<string, string | number | undefined>
		>({
			query: ({ sorting, ...params }) => ({
				url: "users/my-connections",
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: MyConnectionsResponseSchema,
			}),
			providesTags: ["MyConnections"],
		}),
		enrichConnections: builder.mutation<void, { userProspectIds: number[] }>({
			query: (body) => ({
				url: "users/settings/linkedin-enrichment/enrich-now",
				method: "POST",
				body,
			}),
			invalidatesTags: ["MyConnections"],
		}),
		changePassword: builder.mutation<z.infer<typeof ChangePasswordResponseSchema>, ChangePassword>({
			onQueryStarted: (_body, { queryFulfilled }) => {
				const settled = queryFulfilled.then(
					() => {},
					() => {}
				);

				pendingPasswordChanges.add(settled);
				void settled.then(() => pendingPasswordChanges.delete(settled));
			},
			query: (body) => ({
				url: "users/change-password",
				responseSchema: ChangePasswordResponseSchema,
				method: "PATCH",
				body,
			}),
		}),
		location: builder.mutation<User, Location>({
			query: (body) => ({
				url: "users/location",
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["Me"],
		}),
		generateTwoFactorCode: builder.mutation<TwoFactorSetup, void>({
			query: () => ({
				url: "2fa/generate",
				method: "POST",
			}),
		}),
		verifyTwoFactor: builder.mutation<User, TwoFactorAuthentication>({
			query: ({ code }) => ({
				url: "2fa/verify",
				method: "POST",
				body: { code },
			}),
		}),

		upload: builder.mutation<User, FormData>({
			query: (body) => ({
				url: "users/upload",
				method: "POST",
				body: body,
			}),
			invalidatesTags: ["MyConnections"],
		}),
		disableTwoFactor: builder.mutation<User, TwoFactorAuthentication>({
			query: ({ code }) => ({
				url: "2fa/disable",
				method: "POST",
				body: { code },
			}),
		}),
		syncConnections: builder.mutation<{ status: string }, void>({
			query: () => ({
				url: "users/sync-connections",
				method: "POST",
			}),
		}),
		getLinkedinEnrichmentSettings: builder.query<LinkedinEnrichmentSettings, void>({
			query: () => ({
				url: "users/settings/linkedin-enrichment",
				responseSchema: LinkedinEnrichmentSettingsSchema,
			}),
			providesTags: ["LinkedinEnrichmentSettings"],
		}),
		updateLinkedinEnrichmentSettings: builder.mutation<
			LinkedinEnrichmentSettings,
			UpdateLinkedinEnrichmentSettings
		>({
			query: (body) => ({
				url: "users/settings/linkedin-enrichment",
				method: "PATCH",
				body,
				responseSchema: LinkedinEnrichmentSettingsSchema,
			}),
			invalidatesTags: ["LinkedinEnrichmentSettings", "List"],
		}),
		getLinkedinEnrichmentCostPreview: builder.query<
			LinkedinEnrichmentCostPreview,
			LinkedinEnrichmentCostPreviewParams
		>({
			query: (params) => ({
				url: "users/settings/linkedin-enrichment/cost-preview",
				params,
				responseSchema: LinkedinEnrichmentCostPreviewSchema,
			}),
		}),
	}),
});

export const {
	useUpdateMeMutation,
	useChangePasswordMutation,
	useGenerateTwoFactorCodeMutation,
	useVerifyTwoFactorMutation,
	useDisableTwoFactorMutation,
	useUploadMutation,
	useLocationMutation,
	useFindMyConnectionsQuery,
	useSaveOnboardingSettingsMutation,
	useUpdateOnboardingStepMutation,
	useCompleteOnboardingMutation,
	useSaveSlimOnboardingSettingsMutation,
	useSyncConnectionsMutation,
	useGetLinkedinEnrichmentSettingsQuery,
	useUpdateLinkedinEnrichmentSettingsMutation,
	useGetLinkedinEnrichmentCostPreviewQuery,
	useEnrichConnectionsMutation,
	useGetConnectionsFilterOptionsQuery,
} = accountApi;
export const accountApiReducer = accountApi.reducer;
export const accountApiMiddleware = accountApi.middleware;
